import { useState, useEffect, useCallback, useRef } from 'react'
import { useAuth } from './useAuth'

interface SessionMonitorState {
    showWarning: boolean
    secondsRemaining: number
}

const WARNING_THRESHOLD_MS = 120_000

export function useSessionMonitor(): SessionMonitorState & {
    extendSession: () => Promise<void>
} {
    const { isAuthenticated, sessionExpiresAt, extendSession } = useAuth()
    const [showWarning, setShowWarning] = useState(false)
    const [secondsRemaining, setSecondsRemaining] = useState(0)
    const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
    const warningTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    const clearTimers = useCallback(() => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current)
            intervalRef.current = null
        }
        if (warningTimerRef.current) {
            clearTimeout(warningTimerRef.current)
            warningTimerRef.current = null
        }
    }, [])

    useEffect(() => {
        if (!isAuthenticated || !sessionExpiresAt) {
            clearTimers()
            setShowWarning(false)
            return
        }

        const timeUntilExpiry = sessionExpiresAt - Date.now()
        if (timeUntilExpiry <= 0) {
            setShowWarning(false)
            return
        }

        const timeUntilWarning = Math.max(
            0,
            timeUntilExpiry - WARNING_THRESHOLD_MS
        )

        warningTimerRef.current = setTimeout(() => {
            setShowWarning(true)

            intervalRef.current = setInterval(() => {
                const remaining = Math.max(
                    0,
                    Math.ceil((sessionExpiresAt - Date.now()) / 1000)
                )
                setSecondsRemaining(remaining)

                if (remaining <= 0) {
                    clearTimers()
                    setShowWarning(false)
                }
            }, 1000)

            const initialRemaining = Math.max(
                0,
                Math.ceil((sessionExpiresAt - Date.now()) / 1000)
            )
            setSecondsRemaining(initialRemaining)
        }, timeUntilWarning)

        return clearTimers
    }, [isAuthenticated, sessionExpiresAt, clearTimers])

    const handleExtend = useCallback(async () => {
        setShowWarning(false)
        clearTimers()
        await extendSession()
    }, [extendSession, clearTimers])

    return {
        showWarning,
        secondsRemaining,
        extendSession: handleExtend,
    }
}
